import { useQuery } from '@tanstack/react-query';
import { ExternalLink, ShieldAlert, ShieldCheck } from 'lucide-react';
import { api, type ApiError } from '../lib/api';
import { IntervalLogo } from './ui/IntervalLogo';

/**
 * Shown when there is no valid session.
 *
 * There is no password field anywhere in this application, and that is worth
 * saying out loud on this screen: it tells the user where authentication
 * actually happens, and it means a convincing phishing copy of this page has
 * nothing to steal.
 */
export function SignInGate({ error }: { error: ApiError }) {
  const loginUrl = useQuery({ queryKey: ['login-url'], queryFn: api.auth.loginUrl });

  const suspended = error.code === 'USER_SUSPENDED';

  return (
    <div className="flex h-full items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md animate-slide-up">
        <div className="mb-6 flex justify-center">
          <IntervalLogo height={34} />
        </div>

        <div className="panel rounded-xl p-7">
          <div
            className={`mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg ${
              suspended ? 'bg-warn/10 text-warn' : 'bg-accent/10 text-accent'
            }`}
          >
            {suspended ? (
              <ShieldAlert className="h-5 w-5" strokeWidth={1.75} />
            ) : (
              <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
            )}
          </div>

          <h1 className="text-base font-semibold text-ink">
            {suspended ? 'Access not active' : 'Sign in required'}
          </h1>

          <p className="mt-2 text-sm leading-relaxed text-ink-muted">{error.message}</p>

          {!suspended && (
            <>
              <a
                className="btn btn-primary mt-6 w-full py-2 text-sm"
                href={loginUrl.data?.url ?? '#'}
                aria-disabled={!loginUrl.data}
              >
                Continue to the employee portal
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
              </a>

              <p className="mt-4 border-t border-edge pt-4 text-2xs leading-relaxed text-ink-faint">
                This console never asks for your password. You sign in on the employee portal,
                which then sends you back here. If any page claiming to be this tool asks for
                credentials, it is not this tool — report it.
              </p>
            </>
          )}

          {suspended && (
            <p className="mt-6 rounded-md border border-edge bg-surface-sunken p-3 text-2xs leading-relaxed text-ink-muted">
              Your identity was verified, but an administrator has not granted you query access
              yet. Access is approved here rather than in the portal, so this is a separate step.
            </p>
          )}

          <p className="mt-4 font-mono text-2xs text-ink-faint">
            Reference {error.traceId}
          </p>
        </div>
      </div>
    </div>
  );
}
